import { observer } from "mobx-react";
import moment from "moment";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { Colors } from "../../assets";
import { IPlaceOpenHoursRow } from "../../interfaces_and_types";
import { useRootStore } from "../../stores";
import "./styles.css";
import { TimePicker } from "@mui/lab";
import { IconButton, TextField } from "@mui/material";
import LocalizationProvider from "@mui/lab/LocalizationProvider";
import DateAdapter from "@mui/lab/AdapterMoment";
import ClearIcon from "@mui/icons-material/ClearAll";

const PlaceOpenHoursRow: FC<IPlaceOpenHoursRow> = ({
  openHour,
  convertToLocalTimeZone,
  timezone,
}) => {
  const { t, i18n } = useTranslation();
  const { SetPlaceOpenTime } = useRootStore().rootStore.placeStore;
  const [open, setOpen] = useState<moment.Moment | null>(
    openHour.open ? convertToLocalTimeZone(openHour.open, timezone) : null,
  );
  const [close, setClose] = useState<moment.Moment | null>(
    openHour.close ? convertToLocalTimeZone(openHour.close, timezone) : null,
  );

  const time_on_change = async (
    time: moment.Moment | null,
    setter: (v: moment.Moment | null) => void,
    time_type: "open" | "close",
  ) => {
    setter(time);
    let temp_close = close?.clone();
    let temp_open = open?.clone();
    if (time_type === "open" && time !== null && temp_close !== undefined) {
      temp_open = time.clone();
      await SetPlaceOpenTime({
        close: temp_close.subtract(2, "hours").format("HH:mm:ss"),
        open: temp_open.subtract(2, "hours").format("HH:mm:ss"),
        day: openHour.day,
      });
    } else if (
      time_type === "open" &&
      time === null &&
      temp_close === undefined
    ) {
      await SetPlaceOpenTime({
        close: null,
        open: null,
        day: openHour.day,
      });
    } else if (
      time_type === "close" &&
      time !== null &&
      temp_open !== undefined
    ) {
      temp_close = time.clone();
      await SetPlaceOpenTime({
        open: temp_open.subtract(2, "hours").format("HH:mm:ss"),
        close: temp_close.subtract(2, "hours").format("HH:mm:ss"),
        day: openHour.day,
      });
    } else if (
      time_type === "close" &&
      time === null &&
      temp_open === undefined
    ) {
      await SetPlaceOpenTime({
        close: null,
        open: null,
        day: openHour.day,
      });
    }
  };

  return (
    <div className="place-openHour-row">
      <p
        style={{
          color: Colors.LIGHTERPURPLE,
          fontFamily:
            i18n.language === "ar"
              ? "'Cairo', sans-serif"
              : "'Rubik', sans-serif",
          fontWeight: "bold",
        }}
      >
        {t(openHour.day)}
      </p>
      <LocalizationProvider dateAdapter={DateAdapter}>
        <div className="place-openHour-row pickers">
          <div className="place-openHour-row-time-picker">
            <TimePicker
              label={t("Open hour")}
              onChange={(v: moment.Moment | null) => {
                time_on_change(v, setOpen, "open");
              }}
              value={open}
              renderInput={(params: any) => (
                <TextField
                  sx={{ input: { color: Colors.LIGHTERPURPLE } }}
                  {...params}
                />
              )}
              ampm={false}
              clearable
              clearText="Clear"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      time_on_change(null, setOpen, "open");
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </div>
          <div className="place-openHour-row-time-picker">
            <TimePicker
              label={t("Close hour")}
              onChange={(v: moment.Moment | null) =>
                time_on_change(v, setClose, "close")
              }
              value={close}
              renderInput={(params: any) => (
                <TextField
                  sx={{ input: { color: Colors.LIGHTERPURPLE } }}
                  {...params}
                />
              )}
              ampm={false}
              clearable
              clearText="Clear"
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      time_on_change(null, setClose, "close");
                    }}
                  >
                    <ClearIcon />
                  </IconButton>
                ),
              }}
            />
          </div>
        </div>
      </LocalizationProvider>
    </div>
  );
};

export default observer(PlaceOpenHoursRow);
